import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit2, Send, X, Save, Loader2, Lock, Phone, Cake, CalendarCheck, XCircle, Search } from 'lucide-react';
import { auth } from '../config/firebase';
import { clienteService } from '../services/api';
import CriarLembreteAvulso from './CriarLembreteAvulso';

const formatarTelefoneExibicao = (raw) => {
  let value = (raw || '').replace(/\D/g, '');
  if (value.length > 11 && value.startsWith('55')) value = value.slice(2);
  if (value.length > 11) value = value.slice(0, 11);
  if (value.length > 2) value = `(${value.slice(0, 2)}) ${value.slice(2)}`;
  if (value.length > 10) value = `${value.slice(0, 10)}-${value.slice(10)}`;
  return value;
};

const formatarDataNascimento = (iso) => {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.split('T')[0].split('-');
  return `${dia}/${mes}/${ano}`;
};

const ClientesPage = ({ temAcesso }) => {
  const isPremiumPlus = temAcesso('marketing');
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ nome: '', telefone: '', data_nascimento: '' });

  const [clienteLembrete, setClienteLembrete] = useState(null);
  const [busca, setBusca] = useState('');

  const carregarClientes = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await clienteService.listar(token);
      setClientes(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarClientes();
  }, []);

  const abrirModal = (cliente = null) => {
    if (cliente) {
      setEditingId(cliente.id);
      setForm({
        nome: cliente.nome,
        telefone: formatarTelefoneExibicao(cliente.telefone),
        data_nascimento: cliente.data_nascimento ? cliente.data_nascimento.split('T')[0] : '',
      });
    } else {
      setEditingId(null);
      setForm({ nome: '', telefone: '', data_nascimento: '' });
    }
    setIsModalOpen(true);
  };

  const handleTelefoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 11) value = value.slice(0, 11);
    if (value.length > 2) value = `(${value.slice(0, 2)}) ${value.slice(2)}`;
    if (value.length > 10) value = `${value.slice(0, 10)}-${value.slice(10)}`;
    setForm({ ...form, telefone: value });
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const payload = {
        nome: form.nome,
        telefone: form.telefone.replace(/\D/g, ''),
        data_nascimento: form.data_nascimento || null,
      };

      if (editingId) {
        await clienteService.atualizar(editingId, payload, token);
      } else {
        await clienteService.criar(payload, token);
      }

      await carregarClientes();
      setIsModalOpen(false);
    } catch (error) {
      alert(error.response?.data?.message || 'Erro ao salvar cliente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const buscaNormalizada = busca.trim().toLowerCase();
  const buscaDigitos = busca.replace(/\D/g, '');
  const clientesFiltrados = clientes.filter(cliente => {
    if (!buscaNormalizada) return true;
    const nomeCombina = cliente.nome?.toLowerCase().includes(buscaNormalizada);
    const telefoneCombina = buscaDigitos && cliente.telefone?.includes(buscaDigitos);
    return nomeCombina || telefoneCombina;
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center text-slate-400 font-black uppercase text-xs tracking-[0.2em]">
        <Loader2 className="animate-spin text-[#1883FF] mb-4" size={42} />
        <p>Carregando Clientes...</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[950px] mx-auto p-4 box-border font-sans text-left">
      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.05)] w-full">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 border-b border-purple-50 pb-4">
          <div className="flex items-center gap-3">
            <Users size={18} className="text-[#1883FF]" />
            <div>
              <h3 className="text-sm font-black uppercase italic text-slate-900 m-0">Clientes</h3>
              <p className="text-[10px] font-semibold text-slate-500 m-0 mt-1">Histórico de agendamentos, cancelamentos e envio de lembretes.</p>
            </div>
          </div>
          <button
            onClick={() => abrirModal()}
            className="bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-none px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider cursor-pointer flex items-center gap-2 hover:opacity-90 shadow-sm shadow-[#6D28D9]/20 transition-all shrink-0"
          >
            <Plus size={14} /> Adicionar Cliente
          </button>
        </div>

        {clientes.length > 0 && (
          <div className="flex items-center gap-3 bg-[#F8F7FF] border-2 border-transparent rounded-2xl px-4 py-1 mb-6 transition-all duration-300 focus-within:bg-white focus-within:border-[#6D28D9]/30 focus-within:ring-4 focus-within:ring-[#6D28D9]/10">
            <Search size={16} className="text-slate-400 shrink-0" />
            <input
              type="text"
              className="w-full py-3 border-none bg-transparent outline-none text-sm font-semibold text-slate-900 placeholder-slate-400"
              placeholder="Buscar por nome ou telefone..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
        )}

        {clientes.length === 0 ? (
          <div className="bg-[#F8F7FF] border border-dashed border-purple-100 rounded-2xl p-10 flex flex-col items-center justify-center text-center">
            <Users size={32} className="text-slate-300 mb-3" />
            <p className="text-sm font-bold text-slate-500 mb-1">Nenhum cliente cadastrado ainda.</p>
            <p className="text-[10px] font-medium text-slate-400">Clientes aparecem aqui automaticamente conforme agendam (bot, link público ou manual).</p>
          </div>
        ) : clientesFiltrados.length === 0 ? (
          <div className="bg-[#F8F7FF] border border-dashed border-purple-100 rounded-2xl p-10 flex flex-col items-center justify-center text-center">
            <Search size={32} className="text-slate-300 mb-3" />
            <p className="text-sm font-bold text-slate-500 mb-1">Nenhum cliente encontrado.</p>
            <p className="text-[10px] font-medium text-slate-400">Tente buscar por outro nome ou telefone.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {clientesFiltrados.map((cliente) => (
              <div key={cliente.id} className="bg-white border border-slate-100 p-5 rounded-2xl shadow-sm flex flex-col justify-between hover:border-purple-100 hover:shadow-md transition-all">
                <div>
                  <div className="flex justify-between items-start gap-2 mb-3">
                    <h4 className="m-0 text-sm font-black text-slate-800 leading-tight">{cliente.nome}</h4>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => abrirModal(cliente)} title="Editar" className="bg-transparent border-none p-1.5 text-slate-400 hover:text-[#1883FF] hover:bg-[#F5F3FF] rounded-lg cursor-pointer transition-colors">
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => isPremiumPlus && setClienteLembrete(cliente)}
                        disabled={!isPremiumPlus}
                        title={isPremiumPlus ? 'Enviar Lembrete' : 'Disponível no plano Premium'}
                        className={`border-none p-1.5 rounded-lg transition-colors ${isPremiumPlus ? 'bg-transparent text-slate-400 hover:text-[#6D28D9] hover:bg-[#F5F3FF] cursor-pointer' : 'bg-transparent text-slate-300 cursor-not-allowed'}`}
                      >
                        {isPremiumPlus ? <Send size={14} /> : <Lock size={14} />}
                      </button>
                    </div>
                  </div>

                  <p className="flex items-center gap-1.5 text-xs font-bold text-slate-600 mb-1.5">
                    <Phone size={12} className="text-slate-400 shrink-0" /> {formatarTelefoneExibicao(cliente.telefone)}
                  </p>
                  <p className="flex items-center gap-1.5 text-xs font-bold text-slate-600">
                    <Cake size={12} className="text-slate-400 shrink-0" /> {formatarDataNascimento(cliente.data_nascimento)}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-50">
                  <span className="flex items-center gap-1 bg-[#F8F7FF] text-[#1883FF] px-2 py-1 rounded-md text-[10px] font-bold">
                    <CalendarCheck size={12} /> {cliente.total_agendamentos} agendamentos
                  </span>
                  <span className="flex items-center gap-1 bg-red-50 text-red-500 px-2 py-1 rounded-md text-[10px] font-bold">
                    <XCircle size={12} /> {cliente.total_cancelamentos} cancelamentos
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL DE ADICIONAR/EDITAR CLIENTE */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-[1200] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-[440px] rounded-[32px] p-6 sm:p-8 shadow-2xl border border-slate-100 animate-scale-in flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center mb-6 shrink-0">
              <h3 className="font-black text-slate-900 uppercase tracking-wide text-base m-0 flex items-center gap-2">
                <Users size={18} className="text-[#1883FF]" />
                {editingId ? 'Editar Cliente' : 'Adicionar Cliente'}
              </h3>
              <button className="bg-slate-50 border-none p-1.5 rounded-lg text-slate-400 cursor-pointer hover:bg-[#F5F3FF] hover:text-[#6D28D9] transition-colors" onClick={() => setIsModalOpen(false)}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSalvar} className="flex flex-col gap-4 overflow-y-auto pr-1">
              <div className="flex flex-col gap-1.5 text-left">
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Nome *</label>
                <input
                  required type="text"
                  className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-bold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
                  placeholder="Ex: Maria Silva"
                  value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })}
                />
              </div>

              <div className="flex flex-col gap-1.5 text-left">
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Telefone *</label>
                <input
                  required type="text"
                  className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-bold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
                  placeholder="(00) 00000-0000"
                  value={form.telefone} onChange={handleTelefoneChange}
                />
              </div>

              <div className="flex flex-col gap-1.5 text-left">
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Data de Nascimento</label>
                <input
                  type="date"
                  className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-bold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
                  value={form.data_nascimento} onChange={e => setForm({ ...form, data_nascimento: e.target.value })}
                />
              </div>

              <button
                type="submit" disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-none py-4 mt-4 rounded-xl text-[11px] font-black tracking-widest uppercase cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-[#6D28D9]/20 hover:opacity-90 hover:-translate-y-0.5 active:scale-98 transition-all disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
              >
                {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {isSubmitting ? 'SALVANDO...' : 'SALVAR CLIENTE'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DE ENVIO DE LEMBRETE (reaproveita o CriarLembreteAvulso existente) */}
      {clienteLembrete && (
        <div className="fixed inset-0 bg-slate-900/60 z-[1200] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-[700px] rounded-[32px] p-6 sm:p-8 shadow-2xl border border-slate-100 animate-scale-in flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center mb-6 shrink-0">
              <h3 className="font-black text-slate-900 uppercase tracking-wide text-sm m-0 flex items-center gap-2">
                <Send size={16} className="text-[#1883FF]" /> Enviar Lembrete para {clienteLembrete.nome}
              </h3>
              <button className="bg-slate-50 border-none p-1.5 rounded-lg text-slate-400 cursor-pointer hover:bg-[#F5F3FF] hover:text-[#6D28D9] transition-colors" onClick={() => setClienteLembrete(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="overflow-y-auto pr-1">
              <CriarLembreteAvulso clientePreSelecionado={{ nome: clienteLembrete.nome, telefone: clienteLembrete.telefone }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientesPage;
